/**
 * API Contracts: Pair Search Navigation Dropdown
 *
 * TypeScript interface contracts for search functionality.
 * These types serve as the contract between features/pair-search-nav and entities/search layers.
 *
 * Date: 2025-10-28
 * Feature: 001-pair-search-nav
 * Spec: specs/001-pair-search-nav/spec.md
 */

import { ENetwork } from 'web3';

// ============================================================================
// SEARCH RESULT TYPES (Discriminated Union)
// ============================================================================

/**
 * Base properties shared by all search result types.
 * Discriminated by the `type` field for type-safe narrowing.
 */
interface SearchResultBase {
  /** Unique identifier for this search result */
  id: number;

  /** Ranking score (higher = better match). Used for sorting results. */
  matchScore: number;

  /** How the query matched this result */
  matchType: 'exact' | 'prefix' | 'substring';
}

/**
 * Search result for a trading pair (CEX/DEX/SOL).
 * Contains only static data from IProject REST API—no real-time prices.
 */
export interface PairSearchResult extends SearchResultBase {
  type: 'pair';
  pairId: number;
  projectId: number;
  projectName: string;

  /** Trading pair symbol (e.g., "ARIA/USDT", "ETH/USDC") */
  symbol: string;

  /** Base token symbol (e.g., "BTC" from "BTC/USDT") */
  baseToken: string;

  /** Quote token symbol (e.g., "USDT" from "BTC/USDT") */
  quoteToken: string;

  /** Pair type badge */
  pairType: 'cex' | 'dex' | 'sol';

  /** Exchange or DEX platform name (e.g., "binance", "uniswap", "raydium") */
  platform: string;

  /** Blockchain network (only for DEX/SOL pairs) */
  network?: ENetwork;

  /** Whether user has favorited this pair */
  isFavorite: boolean;

  /** Whether the pair belongs to an archived project */
  isArchived: boolean;
}

/**
 * Search result for a project.
 */
export interface ProjectSearchResult extends SearchResultBase {
  type: 'project';
  projectId: number;
  name: string;

  /** Total number of pairs (CEX + DEX + SOL) in this project */
  pairsCount: number;

  /** Project status */
  status: 'active' | 'archived';
}

/**
 * Search result for an arbitrage configuration.
 */
export interface ArbitrageSearchResult extends SearchResultBase {
  type: 'arbitrage';
  arbitrageId: number;
  projectId: number;
  projectName: string;

  /** Human-readable name (e.g., "BTC CEX→DEX Arbitrage") */
  name: string;

  /** Whether the arbitrage belongs to an archived project */
  isArchived: boolean;

  /** Arbitrage status */
  status: 'active' | 'inactive';
}

/**
 * Search result for a balancer task.
 */
export interface BalancerSearchResult extends SearchResultBase {
  type: 'balancer';
  balancerId: number;
  projectId: number;
  projectName: string;
  name: string;

  /** Whether the balancer belongs to an archived project */
  isArchived: boolean;

  /** Balancer status */
  status: 'active' | 'inactive';
}

/**
 * Discriminated union of all search result types.
 * Use type guards (isPairResult, isProjectResult, etc.) to narrow the type.
 */
export type SearchResult =
  | PairSearchResult
  | ProjectSearchResult
  | ArbitrageSearchResult
  | BalancerSearchResult;

// ============================================================================
// SEARCH CATEGORIES
// ============================================================================

/**
 * Enum for categorizing search results in the dropdown.
 * Display order: Projects → Pairs → Arbitrages → Balancers (per FR-005).
 */
export enum SearchCategory {
  PROJECTS = 'projects',
  PAIRS = 'pairs',
  ARBITRAGES = 'arbitrages',
  BALANCERS = 'balancers',
}

/**
 * Display mode for search dropdown - determines which project set is shown.
 * - 'watchlist': Show only favorited items (pairs, arbitrages, balancers)
 * - 'active': Show all active entities (excludes archived)
 * - 'archive': Show only items from archived projects
 */
export type DisplayMode = 'watchlist' | 'active' | 'archive';

/**
 * Entity type filter - applied within the selected display mode.
 * - 'cex'/'dex'/'sol': Filter by pair type
 * - 'arbitrage': Show only arbitrage configurations
 * - 'balancer': Show only balancer tasks
 *
 * Used as array: empty = show all, non-empty = filter by selected types
 */
export type EntityTypeFilter = 'cex' | 'dex' | 'sol' | 'arbitrage' | 'balancer';

/**
 * @deprecated Use DisplayMode and EntityTypeFilter instead.
 * Legacy combined filter type kept for backward compatibility.
 */
export type PairTypeFilter =
  | 'watchlist'
  | 'all'
  | 'cex'
  | 'dex'
  | 'sol'
  | 'arbitrage'
  | 'balancer'
  | 'archive';

// ============================================================================
// FAVORITES & RECENTS STORAGE
// ============================================================================

/**
 * Schema for localStorage persistence of favorites and recently viewed pairs.
 * Stored at key: 'mm-dashboard:pair-search:favorites-recents'
 */
export interface FavoritesRecentsStorage {
  /** Schema version for future migrations */
  version: 1;

  /** Array of favorited pair IDs */
  favorites: number[];

  /** Recently viewed pairs (max 5, newest first) */
  recents: RecentPair[];

  /** ISO timestamp of last update */
  lastUpdated: string;
}

/**
 * Single recent pair entry.
 */
export interface RecentPair {
  pairId: number;
  projectId: number;
  pairType: 'cex' | 'dex' | 'sol';

  /** Unix timestamp (ms) when pair was viewed */
  timestamp: number;
}

// ============================================================================
// SEARCH STATE (Redux Slice)
// ============================================================================

/**
 * Redux state for search dropdown UI (ephemeral, not persisted).
 * Managed by features/pair-search-nav/model/searchSlice.ts
 */
export interface SearchState {
  /** Whether dropdown is open */
  isOpen: boolean;

  /** Current search input value */
  searchQuery: string;

  /** Debounced search query (400ms delay) */
  debouncedQuery: string;

  /** Keyboard navigation focus index (-1 = no focus) */
  focusedIndex: number;

  /** Navigation error to display inline */
  navigationError: NavigationError | null;

  /** Loading indicator for async operations */
  isLoading: boolean;
}

/**
 * Error state for failed navigation attempts.
 * Displayed inline in dropdown (keeps dropdown open per FR-021).
 */
export interface NavigationError {
  /** User-facing error message */
  message: string;

  /** Entity type that failed to load */
  entityType: 'pair' | 'project' | 'arbitrage' | 'balancer';

  /** Entity ID that failed */
  entityId: number;

  /** Timestamp for auto-dismiss after 5s */
  timestamp: number;
}

// ============================================================================
// SEARCH FUNCTIONS (Business Logic Contracts)
// ============================================================================

/**
 * Filter and rank entities based on search query.
 *
 * @param entities - Array of entities to search (projects, pairs, etc.)
 * @param query - User search query (case-insensitive)
 * @param options - Ranking options (exact match boost, volume weight)
 * @returns Filtered and ranked search results
 */
export type FilterEntitiesFunction = <T extends SearchResult>(
  entities: T[],
  query: string,
  options?: RankingOptions,
) => T[];

/**
 * Options for search ranking algorithm.
 */
export interface RankingOptions {
  /** Score boost for exact matches (default: 100) */
  exactMatchBoost?: number;

  /** Score boost for prefix matches (default: 50) */
  prefixMatchBoost?: number;

  /** Score for substring matches (default: 10) */
  substringMatchScore?: number;

  /** Weight multiplier for volume/activity sorting (default: 1) */
  volumeWeight?: number;
}

/**
 * Load favorites and recents from localStorage.
 * Validates schema version and filters out stale pair IDs.
 *
 * @returns Parsed storage object or defaults if invalid/missing
 */
export type LoadFavoritesRecentsFunction = () => FavoritesRecentsStorage;

/**
 * Save favorites and recents to localStorage.
 * Handles quota exceeded and disabled storage gracefully.
 *
 * @param storage - Storage object to persist
 * @returns Success boolean
 */
export type SaveFavoritesRecentsFunction = (storage: FavoritesRecentsStorage) => boolean;

/**
 * Add or remove a pair from favorites.
 *
 * @param pairId - Pair ID to toggle
 * @returns Updated favorites array
 */
export type ToggleFavoriteFunction = (pairId: number) => number[];

/**
 * Track a recently viewed pair.
 * Adds to recents array (max 5), removes oldest if needed.
 *
 * @param pair - Recent pair entry
 */
export type TrackRecentPairFunction = (pair: Omit<RecentPair, 'timestamp'>) => void;

// ============================================================================
// NAVIGATION FUNCTIONS
// ============================================================================

/**
 * Navigate to a search result entity.
 * Handles routing for pairs, projects, arbitrages, and balancers.
 *
 * @param result - Search result to navigate to
 * @returns Promise resolving to navigation success boolean
 */
export type NavigateToResultFunction = (result: SearchResult) => Promise<boolean>;

/**
 * Generate route path for a search result.
 *
 * @param result - Search result
 * @returns React Router path string
 */
export type GetResultRouteFunction = (result: SearchResult) => string;

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for PairSearchResult.
 */
export function isPairResult(result: SearchResult): result is PairSearchResult {
  return result.type === 'pair';
}

/**
 * Type guard for ProjectSearchResult.
 */
export function isProjectResult(result: SearchResult): result is ProjectSearchResult {
  return result.type === 'project';
}

/**
 * Type guard for ArbitrageSearchResult.
 */
export function isArbitrageResult(result: SearchResult): result is ArbitrageSearchResult {
  return result.type === 'arbitrage';
}

/**
 * Type guard for BalancerSearchResult.
 */
export function isBalancerResult(result: SearchResult): result is BalancerSearchResult {
  return result.type === 'balancer';
}

/**
 * Validate localStorage storage structure.
 */
export function isValidStorage(data: unknown): data is FavoritesRecentsStorage {
  if (typeof data !== 'object' || data === null) return false;

  const storage = data as FavoritesRecentsStorage;
  return (
    typeof storage.version === 'number' &&
    Array.isArray(storage.favorites) &&
    Array.isArray(storage.recents) &&
    typeof storage.lastUpdated === 'string'
  );
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * localStorage key for favorites/recents persistence.
 */
export const FAVORITES_RECENTS_STORAGE_KEY = 'mm-dashboard:pair-search:favorites-recents';

/**
 * Maximum number of recent pairs to store.
 */
export const MAX_RECENT_PAIRS = 5;

/**
 * Search input debounce delay (ms).
 */
export const SEARCH_DEBOUNCE_MS = 400;

/**
 * Maximum results per category in dropdown.
 */
export const MAX_RESULTS_PER_CATEGORY = 5;

/**
 * Navigation error auto-dismiss timeout (ms).
 */
export const ERROR_DISMISS_TIMEOUT_MS = 5000;

/**
 * Search query max length (prevent abuse).
 */
export const MAX_SEARCH_QUERY_LENGTH = 100;
